import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  Palette,
  Eye,
  TrendingUp,
  MapPin,
  Award,
  Target,
} from "lucide-react";
import QuoteDialog from "@/components/site/QuoteDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const TITLE = "Création de logo à Paris — EM Group | Agence premium";
const DESCRIPTION =
  "Création de logo à Paris par EM Group : identité visuelle sur-mesure, branding complet, accompagnement stratégique pour entreprises parisiennes ambitieuses. Devis sous 24h.";
const URL =
  typeof window !== "undefined"
    ? `${window.location.origin}/creation-logo-paris`
    : "";

const FAQ_ITEMS = [
  {
    q: "Combien coûte une création de logo à Paris ?",
    a: "Chez EM Group, la création de logo à Paris démarre à 699 € TTC avec le Pack Logo Essentiel (logo, déclinaisons, fichiers HD et 2 visuels offerts). Pour un branding complet (palette, typographies, charte graphique, univers visuel), comptez à partir de 1599 € TTC. Le prix dépend de votre secteur, de votre positionnement et de l'ampleur du projet. Un devis détaillé vous est systématiquement adressé avant toute commande.",
  },
  {
    q: "Pourquoi choisir une agence parisienne pour créer son logo ?",
    a: "Paris est l'un des marchés les plus concurrentiels d'Europe. Un logo amateur y passe inaperçu, voire dessert votre image. EM Group conçoit des logos pensés pour le niveau d'exigence parisien : esthétique premium, cohérence de marque, fichiers vectoriels professionnels et accompagnement stratégique. Vous ne recevez pas un template — vous obtenez une vraie identité construite pour durer.",
  },
  {
    q: "Combien de temps pour créer un logo professionnel à Paris ?",
    a: "Comptez 5 à 10 jours pour un Pack Logo Essentiel et 2 à 3 semaines pour un Pack Branding Complet. Ce délai inclut un brief approfondi, plusieurs pistes créatives, les révisions et la livraison des fichiers finaux. Une production accélérée est possible pour les lancements ou levées de fonds urgents — précisez-le dès le premier échange.",
  },
  {
    q: "Travaillez-vous uniquement à Paris ou aussi à distance ?",
    a: "Nous intervenons dans tout Paris et en Île-de-France (La Défense, Neuilly, Boulogne-Billancourt, Saint-Denis…) en visio, téléphone ou rendez-vous physique selon vos préférences. Nous accompagnons également des clients partout en France et à l'international. Le processus reste identique : brief stratégique, propositions, révisions et livraison.",
  },
  {
    q: "Puis-je faire évoluer mon logo plus tard ?",
    a: "Absolument. Les fichiers sources vectoriels vous sont toujours remis, ce qui vous permet de faire évoluer votre logo à tout moment : refresh, déclinaisons, nouvelles versions. Vous pouvez aussi passer du Pack Logo au Pack Branding Complet, ou basculer sur un accompagnement 360° lorsque votre entreprise grandit.",
  },
];

function setMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function CreationLogoParis() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    setMeta("description", DESCRIPTION);
    setMeta(
      "keywords",
      "création de logo Paris, logo Paris, branding Paris, identité visuelle Paris, agence digitale Paris, graphiste Paris, création logo entreprise Paris"
    );
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESCRIPTION, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:locale", "fr_FR", "property");
    if (URL) setMeta("og:url", URL, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", TITLE);
    setMeta("twitter:description", DESCRIPTION);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    if (URL) canonical.setAttribute("href", URL);

    // JSON-LD Service
    let ld = document.getElementById("ld-logo-paris");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "ld-logo-paris";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Création de logo et identité visuelle",
      provider: {
        "@type": "LocalBusiness",
        name: "EM Group",
        email: "contact@emgroupdigital.com",
        telephone: "+262262667970",
        url: URL || "https://emgroupdigital.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "60 rue François 1er",
          addressLocality: "Paris",
          postalCode: "75008",
          addressCountry: "FR",
        },
        areaServed: [
          { "@type": "Place", name: "Paris" },
          { "@type": "Place", name: "Île-de-France" },
          { "@type": "Place", name: "France" },
        ],
      },
      areaServed: { "@type": "City", name: "Paris" },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        valueAddedTaxIncluded: true,
        price: "699",
        description:
          "Pack Logo Essentiel : logo + déclinaisons + 2 visuels offerts.",
      },
      description: DESCRIPTION,
    });

    // JSON-LD FAQ
    let ldFaq = document.getElementById("ld-logo-paris-faq");
    if (!ldFaq) {
      ldFaq = document.createElement("script");
      ldFaq.type = "application/ld+json";
      ldFaq.id = "ld-logo-paris-faq";
      document.head.appendChild(ldFaq);
    }
    ldFaq.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    return () => {
      document.title = prevTitle;
    };
  }, []);

  const openQuote = () => setQuoteOpen(true);

  return (
    <div className="min-h-screen bg-[#050507] text-white" data-testid="page-logo-paris">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-home-logo">
            <img
              src="/brand/logo-em-group.png"
              alt="EM Group"
              className="h-10 w-10 object-contain"
            />
            <span className="font-outfit tracking-tight text-lg">
              EM <span className="text-white/50">Group</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft size={16} /> Accueil
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(229,36,49,0.2),transparent_55%),radial-gradient(ellipse_at_70%_70%,rgba(31,44,110,0.25),transparent_55%)]" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8">
            <MapPin size={14} className="text-[#E52431]" />
            <span className="text-xs uppercase tracking-[0.2em] text-white/75">
              Paris · Île-de-France
            </span>
          </div>

          <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05]">
            Création de logo{" "}
            <span className="gradient-text font-medium">à Paris</span>
            <br className="hidden md:block" />
            <span className="text-white/85">pour entreprises ambitieuses</span>
          </h1>

          <p className="mt-8 text-white/75 text-lg md:text-xl font-light leading-relaxed max-w-3xl">
            <strong className="text-white font-normal">
              À Paris, l&rsquo;image fait tout.
            </strong>{" "}
            Dans un marché saturé où des milliers d&rsquo;entreprises se
            disputent l&rsquo;attention, un logo amateur fait douter — un logo
            professionnel fait vendre.
          </p>

          <p className="mt-5 text-white/65 text-base md:text-lg font-light leading-relaxed max-w-3xl">
            EM Group conçoit des logos sur-mesure pour les entrepreneurs,
            start-ups, PME et marques basés à{" "}
            <strong className="text-white font-normal">Paris et en Île-de-France</strong>,
            pensés pour incarner votre positionnement et{" "}
            <strong className="text-white font-normal">
              convertir vos prospects en clients
            </strong>
            .
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={openQuote}
              data-testid="hero-cta-logo-paris"
              className="inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-10 py-5 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
            >
              Créer mon logo dès maintenant
              <ArrowRight size={16} />
            </button>
            <a
              href="#packs"
              data-testid="hero-secondary-packs"
              className="inline-flex items-center gap-2 rounded-full glass text-white font-medium px-8 py-5 text-sm uppercase tracking-[0.15em] hover:bg-white/10 transition-all duration-300"
            >
              Voir les tarifs
            </a>
          </div>
          <p className="mt-5 text-xs text-white/50 font-light">
            ⚡ Réponse sous 24h · Devis personnalisé · Sans engagement
          </p>
        </div>
      </section>

      {/* Service presentation */}
      <section className="py-20 md:py-28 section-light">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Le service
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Un logo conçu pour exister dans la capitale
          </h2>
          <div className="mt-8 space-y-5 text-[#0A0A0A]/70 text-lg font-light leading-relaxed">
            <p>
              Un logo n&rsquo;est pas un visuel décoratif. C&rsquo;est la{" "}
              <span className="text-[#0A0A0A] font-normal">
                signature visuelle de votre entreprise
              </span>{" "}
              — le premier signal que vos prospects parisiens reçoivent
              avant même de vous connaître.
            </p>
            <p>
              Chez EM Group, chaque{" "}
              <strong className="text-[#0A0A0A] font-normal">
                création de logo à Paris
              </strong>{" "}
              commence par une analyse stratégique : votre secteur, vos
              concurrents, votre cible et vos ambitions. Nous produisons
              ensuite un logo cohérent, impactant et durable, capable de
              tenir face à la concurrence parisienne.
            </p>
            <p>
              Notre approche ne s&rsquo;arrête pas au design. Nous vous
              accompagnons de l&rsquo;identité visuelle jusqu&rsquo;à vos
              premiers clients, avec une vision 360° et une exécution
              pensée pour la performance.
            </p>
            <p>
              <a
                href="#accompagnement"
                data-testid="service-link-accompagnement"
                className="inline-flex items-center gap-2 text-[#E52431] font-medium hover:underline"
              >
                Découvrir notre accompagnement complet →
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Branding importance */}
      <section className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
                — Branding & concurrence
              </span>
              <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
                À Paris, votre image est votre{" "}
                <span className="gradient-text font-medium">premier argument</span>.
              </h2>
              <div className="mt-8 space-y-5 text-white/70 text-lg font-light leading-relaxed">
                <p>
                  Paris concentre plus de{" "}
                  <strong className="text-white font-normal">
                    400 000 entreprises
                  </strong>{" "}
                  sur un territoire restreint. Dans ce bruit permanent, les
                  clients décident en quelques secondes s&rsquo;ils font
                  confiance à une marque — ou passent à la suivante.
                </p>
                <p>
                  Selon plusieurs études en neurosciences de la consommation,
                  plus de{" "}
                  <strong className="text-white font-normal">
                    60% des décisions d&rsquo;achat
                  </strong>{" "}
                  sont influencées par la perception visuelle d&rsquo;une
                  marque. Un logo professionnel, combiné à une identité
                  cohérente, crédibilise votre offre et{" "}
                  <strong className="text-white font-normal">
                    déclenche l&rsquo;acte d&rsquo;achat
                  </strong>
                  .
                </p>
                <p>
                  Sur un marché aussi exigeant que Paris, l&rsquo;amateurisme
                  se remarque immédiatement. Un branding solide
                  n&rsquo;est plus un confort — c&rsquo;est une condition
                  d&rsquo;entrée.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-gradient-to-br from-[#1a0f12] to-[#0B1030] border border-white/10 p-8 space-y-5">
                {[
                  { icon: Eye, label: "Reconnaissance immédiate" },
                  { icon: Award, label: "Crédibilité premium" },
                  { icon: TrendingUp, label: "Hausse du taux de conversion" },
                  { icon: Target, label: "Différenciation concurrentielle" },
                ].map((it) => {
                  const Icon = it.icon;
                  return (
                    <div key={it.label} className="flex items-center gap-4">
                      <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#E52431]/20 to-[#1F2C6E]/30 border border-white/10 grid place-items-center flex-shrink-0">
                        <Icon size={18} className="text-white" strokeWidth={1.5} />
                      </div>
                      <span className="text-white font-light">{it.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packs */}
      <section id="packs" className="py-20 md:py-28 section-light">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Nos packs logo & branding
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Une création de logo{" "}
            <span className="gradient-text font-medium">pour chaque ambition</span>
          </h2>
          <p className="mt-6 text-[#0A0A0A]/60 text-lg font-light max-w-2xl">
            Du logo seul au branding complet, nous proposons des offres
            claires et transparentes adaptées aux exigences des entreprises
            parisiennes.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                name: "Pack Logo Essentiel",
                price: "699 € TTC",
                items: [
                  "Logo sur-mesure",
                  "Déclinaisons réseaux",
                  "Adaptation couleurs",
                  "Fichiers HD & sources",
                  "2 visuels offerts",
                ],
              },
              {
                name: "Pack Branding Complet",
                price: "À partir de 1599 € TTC",
                highlight: true,
                items: [
                  "Logo + déclinaisons",
                  "Palette couleurs",
                  "Typographies",
                  "Charte graphique",
                  "Univers visuel",
                  "Templates",
                ],
              },
              {
                name: "Pack Visibilité",
                price: "À partir de 2199 € TTC",
                items: [
                  "Branding complet",
                  "4 visuels réseaux",
                  "Mise en place réseaux",
                  "Accompagnement lancement",
                ],
              },
            ].map((p) => (
              <div
                key={p.name}
                data-testid={`pack-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`relative rounded-3xl p-8 transition-all duration-500 ${
                  p.highlight
                    ? "bg-gradient-to-b from-[#0A0A0A] to-[#141418] text-white border border-[#E52431]/40 glow-red"
                    : "light-card"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] gradient-bg text-white font-semibold shadow-[0_8px_20px_rgba(229,36,49,0.35)]">
                      Le plus choisi
                    </div>
                  </div>
                )}
                <h3
                  className={`font-outfit text-xl font-medium ${
                    p.highlight ? "text-white" : "text-[#0A0A0A]"
                  }`}
                >
                  {p.name}
                </h3>
                <div
                  className={`mt-4 font-outfit text-4xl font-light ${
                    p.highlight ? "gradient-text" : "text-[#0A0A0A]"
                  }`}
                >
                  {p.price}
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.items.map((f) => (
                    <li
                      key={f}
                      className={`flex items-start gap-3 text-sm ${
                        p.highlight ? "text-white/80" : "text-[#0A0A0A]/70"
                      }`}
                    >
                      <Check
                        size={15}
                        className="text-[#E52431] mt-0.5 flex-shrink-0"
                      />
                      <span className="font-light">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={openQuote}
                  data-testid={`pack-cta-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`mt-7 w-full rounded-full py-3 font-semibold text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                    p.highlight
                      ? "gradient-bg text-white hover:shadow-[0_0_40px_rgba(229,36,49,0.5)]"
                      : "bg-[#0A0A0A] text-white hover:bg-[#E52431]"
                  }`}
                >
                  Demander un devis
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accompagnement highlight */}
      <section id="accompagnement" className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div
            className="relative rounded-3xl p-10 md:p-14 overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #0A0A0A 0%, #160812 35%, #0B1030 75%, #0A0A0A 100%)",
            }}
          >
            <div className="absolute -top-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#E52431]/25 blur-[100px]" />
            <div className="absolute -bottom-32 -right-20 w-[400px] h-[400px] rounded-full bg-[#1F2C6E]/30 blur-[100px]" />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full gradient-bg text-[10px] uppercase tracking-[0.2em] text-white font-semibold mb-6">
                <Sparkles size={11} /> L&rsquo;offre phare
              </div>

              <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.1]">
                Envie d&rsquo;aller{" "}
                <span className="gradient-text font-medium">plus loin</span> que
                le logo&nbsp;?
              </h2>
              <p className="mt-6 text-white/75 text-lg font-light max-w-2xl">
                L&rsquo;Accompagnement complet EM Group prend en charge chaque
                étape du lancement ou de la montée en puissance de votre
                entreprise à Paris&nbsp;: branding, visuels, site web,
                stratégie, communication et acquisition. Un seul interlocuteur,
                une équipe senior dédiée.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl">
                {[
                  "Branding complet sur-mesure",
                  "Création de site web",
                  "Visuels & stratégie de communication",
                  "Lancement & acquisition",
                ].map((f) => (
                  <div
                    key={f}
                    className="flex items-center gap-3 text-white/85"
                  >
                    <Check
                      size={15}
                      className="text-[#E52431] flex-shrink-0"
                    />
                    <span className="font-light text-sm md:text-base">
                      {f}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/55">
                    À partir de
                  </div>
                  <div className="font-outfit text-5xl font-light text-white mt-1">
                    3490 € TTC
                  </div>
                </div>
                <button
                  onClick={openQuote}
                  data-testid="accompagnement-cta"
                  className="sm:ml-auto inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-8 py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
                >
                  Être accompagné
                  <ArrowRight size={16} />
                </button>
              </div>

              <p className="mt-6 text-white/55 text-sm font-light">
                <a
                  href="/#accompagnement"
                  data-testid="accompagnement-internal-link"
                  className="text-[#E52431] font-medium hover:underline"
                >
                  En savoir plus sur l&rsquo;accompagnement complet EM Group →
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 section-light">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Ce que vous y gagnez
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Les bénéfices d&rsquo;un{" "}
            <span className="gradient-text font-medium">
              logo professionnel
            </span>
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Award,
                title: "Professionnalisme instantané",
                desc: "Un logo de qualité positionne votre entreprise au niveau des standards parisiens dès le premier regard.",
              },
              {
                icon: Palette,
                title: "Une image cohérente",
                desc: "Une identité visuelle uniforme sur vos réseaux, votre site, vos supports print et vos pitch decks crée la confiance.",
              },
              {
                icon: TrendingUp,
                title: "Plus de clients, plus vite",
                desc: "Une marque forte attire, rassure et convertit mieux qu'une communication improvisée — surtout sur un marché tendu comme Paris.",
              },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="light-card light-card-hover rounded-3xl p-8 transition-all duration-500"
                >
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#E52431]/15 to-[#1F2C6E]/15 border border-[#E52431]/20 grid place-items-center mb-5">
                    <Icon size={20} className="text-[#E52431]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-outfit text-xl font-medium text-[#0A0A0A]">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-[#0A0A0A]/65 font-light leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Impact / Data */}
      <section className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — L&rsquo;impact du visuel
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
            Le design n&rsquo;est pas un détail —{" "}
            <span className="gradient-text font-medium">c&rsquo;est un levier business</span>.
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                kpi: "50 ms",
                title: "pour juger une marque",
                desc: "Les internautes se forgent une première opinion sur votre entreprise en moins de 50 millisecondes (étude Google).",
              },
              {
                kpi: "+23%",
                title: "de revenus",
                desc: "Les marques à forte cohérence visuelle génèrent jusqu'à 23% de revenus supplémentaires (source : Lucidpress).",
              },
              {
                kpi: "81%",
                title: "de reconnaissance",
                desc: "81% des consommateurs doivent avoir confiance dans une marque avant d'acheter — et ça commence par le logo.",
              },
            ].map((s) => (
              <div
                key={s.kpi}
                className="rounded-3xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/10 p-8 hover:border-white/20 transition-all duration-500"
              >
                <div className="font-outfit text-5xl font-light gradient-text">
                  {s.kpi}
                </div>
                <div className="mt-2 text-white font-medium">{s.title}</div>
                <p className="mt-3 text-white/60 font-light leading-relaxed text-sm">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local SEO - Paris districts */}
      <section
        data-testid="local-districts"
        className="py-20 md:py-28 section-light border-t border-black/5"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
              — Présence parisienne
            </span>
            <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
              EM Group accompagne les{" "}
              <span className="gradient-text font-medium">
                entreprises parisiennes
              </span>
            </h2>
            <div className="mt-6 space-y-4 text-[#0A0A0A]/70 text-lg font-light leading-relaxed">
              <p>
                Nous intervenons dans{" "}
                <strong className="text-[#0A0A0A] font-normal">tous les arrondissements de Paris</strong>,
                aux côtés d&rsquo;entrepreneurs, commerçants, cabinets de
                conseil, start-ups et PME qui souhaitent élever leur marque
                au niveau d&rsquo;exigence de la capitale.
              </p>
              <p>
                De Paris centre au quartier d&rsquo;affaires de La Défense,
                nos projets de création de logo et de branding
                s&rsquo;adaptent à chaque secteur — luxe, tech, services,
                restauration, immobilier, santé, retail.
              </p>
            </div>
          </div>

          {/* District chips */}
          <div className="mt-12 flex flex-wrap gap-3">
            {[
              "Paris centre (1er–4e)",
              "Champs-Élysées (8e)",
              "Opéra (9e)",
              "Marais (3e–4e)",
              "Saint-Germain (6e)",
              "Montparnasse (14e–15e)",
              "Bastille (11e–12e)",
              "Canal Saint-Martin (10e)",
              "Batignolles (17e)",
              "Montmartre (18e)",
              "La Défense",
              "Neuilly-sur-Seine",
              "Boulogne-Billancourt",
              "Levallois-Perret",
              "Saint-Denis (93)",
            ].map((city, i) => (
              <motion.span
                key={city}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.03 }}
                data-testid={`district-chip-${i}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/5 border border-black/10 text-[#0A0A0A]/80 text-sm font-light hover:border-[#E52431]/40 hover:text-[#0A0A0A] hover:bg-black/10 transition-all duration-300"
              >
                <MapPin size={12} className="text-[#E52431]" />
                {city}
              </motion.span>
            ))}
          </div>

          <p className="mt-10 text-[#0A0A0A]/60 text-base font-light">
            Vous êtes basé à Paris ou en Île-de-France et votre zone
            n&rsquo;est pas citée ? Nous couvrons la capitale dans son
            ensemble et travaillons également <strong className="text-[#0A0A0A] font-normal">à
            distance partout en France et à l&rsquo;international</strong> —{" "}
            <button
              onClick={openQuote}
              data-testid="local-cta-parlons"
              className="text-[#E52431] font-medium hover:underline"
            >
              parlons de votre projet
            </button>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 section-light" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Questions fréquentes
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Création de logo à Paris :{" "}
            <span className="gradient-text font-medium">tout ce qu&rsquo;il faut savoir</span>
          </h2>
          <p className="mt-6 text-[#0A0A0A]/60 text-lg font-light">
            Les réponses aux questions que se posent les entrepreneurs
            parisiens avant de lancer leur projet d&rsquo;identité visuelle.
          </p>

          <div className="mt-12">
            <Accordion
              type="single"
              collapsible
              defaultValue="faq-0"
              className="space-y-3"
            >
              {FAQ_ITEMS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="light-card rounded-2xl px-6 border border-black/5"
                >
                  <AccordionTrigger className="text-left font-outfit text-lg font-medium text-[#0A0A0A] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#0A0A0A]/70 font-light leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={openQuote}
              data-testid="faq-final-cta"
              className="inline-flex items-center gap-3 rounded-full gradient-bg text-white font-semibold px-10 py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
            >
              Démarrer mon projet
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Voir aussi - Internal linking */}
      <section
        data-testid="voir-aussi-section"
        className="py-20 md:py-24 bg-[#0A0A0A] border-t border-white/5"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Pour aller plus loin
          </span>
          <h2 className="mt-4 font-outfit text-3xl md:text-4xl font-light tracking-tight text-white">
            Explorer l&rsquo;univers{" "}
            <span className="gradient-text font-medium">EM Group</span>
          </h2>
          <p className="mt-4 text-white/60 font-light max-w-2xl">
            La création de logo n&rsquo;est qu&rsquo;une brique. Découvrez
            l&rsquo;ensemble des services pensés pour faire grandir votre
            entreprise.
          </p>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <a
              href="/#branding"
              data-testid="voir-aussi-branding"
              className="group rounded-3xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/10 p-7 hover:border-[#E52431]/40 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                Branding
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-white">
                Packs branding &amp; visuels
              </h3>
              <p className="mt-3 text-white/60 font-light text-sm leading-relaxed">
                Logos, chartes graphiques, packs réseaux sociaux et
                déclinaisons — tout l&rsquo;éventail branding EM Group.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir les packs <ArrowRight size={14} />
              </span>
            </a>

            <a
              href="/#sites-web"
              data-testid="voir-aussi-sites-web"
              className="group rounded-3xl bg-gradient-to-b from-white/[0.04] to-transparent border border-white/10 p-7 hover:border-[#E52431]/40 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                Web
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-white">
                Création de sites web
              </h3>
              <p className="mt-3 text-white/60 font-light text-sm leading-relaxed">
                Vitrines, one-pages et e-commerce sur-mesure — pour prolonger
                votre identité visuelle sur le digital.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Découvrir <ArrowRight size={14} />
              </span>
            </a>

            <Link
              to="/creation-logo-la-reunion"
              data-testid="voir-aussi-logo-reunion"
              className="group rounded-3xl bg-gradient-to-b from-[#E52431]/[0.08] to-transparent border border-[#E52431]/20 p-7 hover:border-[#E52431]/50 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                SEO local
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-white">
                Création de logo à La Réunion
              </h3>
              <p className="mt-3 text-white/60 font-light text-sm leading-relaxed">
                Vous avez un projet dans le 974 ? Découvrez notre offre
                dédiée aux entreprises réunionnaises.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir la page <ArrowRight size={14} />
              </span>
            </Link>

            <Link
              to="/creation-logo-ile-maurice"
              data-testid="voir-aussi-logo-maurice"
              className="group rounded-3xl bg-gradient-to-b from-[#1F2C6E]/[0.12] to-transparent border border-[#1F2C6E]/30 p-7 hover:border-[#1F2C6E]/60 transition-all duration-500"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E52431]">
                SEO local
              </div>
              <h3 className="mt-3 font-outfit text-xl font-medium text-white">
                Création de logo à l&rsquo;île Maurice
              </h3>
              <p className="mt-3 text-white/60 font-light text-sm leading-relaxed">
                Vous lancez un projet à Maurice ? Découvrez notre offre
                dédiée aux entreprises mauriciennes.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#E52431] text-sm font-medium group-hover:gap-3 transition-all">
                Voir la page <ArrowRight size={14} />
              </span>
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 text-sm">
            <a
              href="/#accompagnement"
              data-testid="voir-aussi-pill-accompagnement"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 font-light hover:border-[#E52431]/40 hover:text-white transition-all"
            >
              Accompagnement complet
            </a>
            <a
              href="/#tarifs"
              data-testid="voir-aussi-pill-tarifs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 font-light hover:border-[#E52431]/40 hover:text-white transition-all"
            >
              Tarifs
            </a>
            <a
              href="/#audit"
              data-testid="voir-aussi-pill-audit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 font-light hover:border-[#E52431]/40 hover:text-white transition-all"
            >
              Audit 190 € TTC
            </a>
            <a
              href="/#faq"
              data-testid="voir-aussi-pill-faq"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 font-light hover:border-[#E52431]/40 hover:text-white transition-all"
            >
              FAQ générale
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #E52431 0%, #7E1945 35%, #2A2A5F 70%, #1F2C6E 100%)",
        }}
      >
        <div className="absolute inset-0 bg-[#0A0A0A]/20" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
            Votre{" "}
            <span className="font-medium italic">nouveau logo</span> commence
            ici.
          </h2>
          <p className="mt-6 text-white/80 text-lg font-light max-w-2xl mx-auto">
            Parlez-nous de votre projet à Paris. Nous revenons vers vous
            sous 24h avec un plan personnalisé et un devis précis.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={openQuote}
              data-testid="final-cta-logo-paris"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0A0A] font-semibold px-10 py-5 text-sm uppercase tracking-[0.15em] hover:scale-[1.03] hover:shadow-[0_0_50px_rgba(255,255,255,0.3)] transition-all duration-300 btn-shine"
            >
              Créer mon logo dès maintenant
              <ArrowRight size={16} />
            </button>
            <a
              href="tel:+262262667970"
              data-testid="final-cta-phone"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold px-10 py-5 text-sm uppercase tracking-[0.15em] hover:bg-white/20 transition-all duration-300"
            >
              +262 262 66 79 70
            </a>
          </motion.div>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-white/70">
            Paris · France · International · Réponse sous 24h
          </p>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="py-10 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div>
            © {new Date().getFullYear()} EM Group — Agence digitale premium.
          </div>
          <div className="flex gap-5">
            <Link to="/mentions-legales" className="hover:text-white/80">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="hover:text-white/80">
              Confidentialité
            </Link>
            <Link to="/cgv" className="hover:text-white/80">
              CGV
            </Link>
          </div>
        </div>
      </footer>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
